import { UppFloorPlanBar } from '../plan/floor-plan-element';
import type { UppFloorPlanTheme } from '../plan/floor-plan-theme';
import {
  registerFloorPlanShapePainter,
  type FloorPlanShapePaintContext,
} from './paint';

/**
 * English comment normalized.
 * English comment normalized.
 */
const V3_BAR_SIZE_CM = {
  section_height: 20,
  section_width: 40,
  customer_height: 50,
  backend_height: 25,
  appliance_width: 60,
  sink_width: 40,
  margin_width: 40,
  stool_diameter: 36,
  stool_spacing: 75,
  /** v3 uses this value in `_drawStool` as offset from the base; the actual center is recalculated to avoid overlap. */
  stool_gap: 30,
} as const;

/** Free gap between the bottom edge of the counter and the top edge of the stool (cm). */
const STOOL_CLEARANCE_BELOW_BAR_CM = 10;

function t(scalePxPerCm: number, cm: number): number {
  return cm * scalePxPerCm;
}

interface V3BarDimPx {
  section_height: number;
  section_width: number;
  customer_height: number;
  backend_height: number;
  appliance_width: number;
  sink_width: number;
  margin_width: number;
  stool_diameter: number;
  stool_spacing: number;
  stool_gap: number;
}

function v3BarDimensionsPx(scalePxPerCm: number): V3BarDimPx {
  const d = V3_BAR_SIZE_CM;
  return {
    section_height: t(scalePxPerCm, d.section_height),
    section_width: t(scalePxPerCm, d.section_width),
    customer_height: t(scalePxPerCm, d.customer_height),
    backend_height: t(scalePxPerCm, d.backend_height),
    appliance_width: t(scalePxPerCm, d.appliance_width),
    sink_width: t(scalePxPerCm, d.sink_width),
    margin_width: t(scalePxPerCm, d.margin_width),
    stool_diameter: t(scalePxPerCm, d.stool_diameter),
    stool_spacing: t(scalePxPerCm, d.stool_spacing),
    stool_gap: t(scalePxPerCm, d.stool_gap),
  };
}

function isDarkFloorTheme(theme: UppFloorPlanTheme): boolean {
  const m = theme.roomFill.match(/^#([0-9a-f]{6})$/i);
  if (!m) {
    return false;
  }
  const n = parseInt(m[1], 16);
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  return r * 0.299 + g * 0.587 + b * 0.114 < 96;
}

/**
 * Reference palette for counter (floor on light/dark background): counter #8ca0b8, work strip, stools outline only.
 */
function barReferencePalette(theme: UppFloorPlanTheme): {
  counterMain: string;
  counterMuted: string;
  workspaceFill: string;
  workStrip: string;
  segmentStroke: string;
  outline: string;
  shelfLight: string;
  stoolStroke: string;
  sinkBasin: string;
  sinkStroke: string;
  sinkTap: string;
  applianceBody: string;
  applianceDoor: string;
  backStrip: string;
} {
  const dark = isDarkFloorTheme(theme);
  return {
    counterMain: '#8ca0b8',
    counterMuted: '#6e7f94',
    workspaceFill: dark ? '#3d4a58' : '#e4e7ec',
    workStrip: dark ? '#8f98a3' : '#a8abb3',
    segmentStroke: dark ? '#1e242c' : '#4a5568',
    outline: dark ? '#2a3340' : '#4d5d6e',
    shelfLight: dark ? '#7e8894' : '#c5cad3',
    stoolStroke: dark ? '#b8d0ea' : '#5a7a9c',
    sinkBasin: dark ? '#d8dee8' : '#ffffff',
    sinkStroke: dark ? '#1a1e24' : '#2d3748',
    sinkTap: dark ? '#9aa5b5' : '#4a5568',
    applianceBody: dark ? '#6b7582' : '#b8bcc4',
    applianceDoor: dark ? '#a8b0bd' : '#f7f8fa',
    backStrip: dark ? '#5a6570' : '#9ca3af',
  };
}

/** English comment normalized. */
function v3InnerAfterShape(
  w: number,
  h: number,
  ch: number,
  shape: string,
): { ix: number; iy: number; iw: number; ih: number } {
  let ix = 0;
  const iy = 0;
  let iw = w;
  const ih = h - ch;
  switch (shape) {
    case 'TOLEFT':
      ix += ch;
      iw -= ch;
      break;
    case 'TORIGHT':
      iw -= ch;
      break;
    case 'USHAPE':
      ix += ch;
      iw -= 2 * ch;
      break;
    default:
      break;
  }
  return { ix, iy, iw, ih };
}

/** English comment normalized. */
function v3InnerShrunkForBack(
  inner: { ix: number; iy: number; iw: number; ih: number },
  shape: string,
): { ix: number; iy: number; iw: number; ih: number } {
  let { ix, iy, iw } = inner;
  const { ih } = inner;
  ix -= 1;
  iy -= 1;
  if (shape === 'TOLEFT' || shape === 'USHAPE') {
    ix += 2;
  }
  if (shape === 'USHAPE') {
    iw -= 2;
  }
  return { ix, iy, iw, ih };
}

function drawV3Sink(
  K: any,
  parent: any,
  x: number,
  y: number,
  sw: number,
  bh: number,
  pal: ReturnType<typeof barReferencePalette>,
): void {
  const sg = sw * 0.1;
  const sh = bh * 0.7;
  const swIn = sw - sg * 2;
  const sx = x + sw * 0.1;
  const sy = y + bh - sh;
  const fr = swIn * 0.1;
  const fx = sx + swIn * 0.5;
  const fy = sy + sh * 0.55;
  parent.add(
    new K.Rect({
      x: sx,
      y: sy,
      width: swIn,
      height: sh,
      fill: pal.sinkBasin,
      stroke: pal.sinkStroke,
      strokeWidth: 1,
      listening: false,
    }),
  );
  parent.add(
    new K.Line({
      points: [fx, fy, fx, sy + sh],
      stroke: pal.sinkStroke,
      strokeWidth: 1,
      listening: false,
    }),
  );
  parent.add(
    new K.Circle({
      x: fx,
      y: fy,
      radius: fr,
      fill: pal.sinkTap,
      listening: false,
    }),
  );
}

function drawV3Appliance(
  K: any,
  parent: any,
  x: number,
  y: number,
  aw: number,
  bh: number,
  pal: ReturnType<typeof barReferencePalette>,
): void {
  const ah = bh * 1.2 - bh;
  const dh = ah * 0.2;
  parent.add(
    new K.Rect({
      x,
      y: y - ah,
      width: aw,
      height: bh + ah,
      fill: pal.applianceBody,
      stroke: pal.segmentStroke,
      strokeWidth: 0.5,
      listening: false,
    }),
  );
  parent.add(
    new K.Rect({
      x,
      y: y - dh,
      width: aw,
      height: dh,
      fill: pal.applianceDoor,
      listening: false,
    }),
  );
}

function drawV3BackBar(
  K: any,
  parent: any,
  ix: number,
  iy: number,
  iw: number,
  ih: number,
  dim: V3BarDimPx,
  pal: ReturnType<typeof barReferencePalette>,
): void {
  const sw = dim.section_width;
  const sh = dim.section_height;
  const ns = Math.max(1, Math.round(iw / sw));
  for (let i = 0; i < ns; i++) {
    parent.add(
      new K.Rect({
        x: ix + (i * iw) / ns,
        y: iy + 1,
        width: iw / ns,
        height: sh,
        fill: pal.workStrip,
        stroke: pal.segmentStroke,
        strokeWidth: 1,
        listening: false,
      }),
    );
  }
}

function drawV3BackEnd(
  K: any,
  parent: any,
  ix: number,
  iy: number,
  iw: number,
  ih: number,
  dim: V3BarDimPx,
  pal: ReturnType<typeof barReferencePalette>,
  options?: { omitWorkspaceFill?: boolean },
): void {
  const bh = dim.backend_height;
  const aw = dim.appliance_width;
  const sw = dim.sink_width;
  const mw = dim.margin_width;
  const pattern = ['appliance', 'sink', 'margin', 'appliance'] as const;
  const sizes: Record<string, number> = {
    appliance: aw,
    sink: sw,
    margin: mw,
  };
  const stripY = iy + ih - bh - 1;

  if (!options?.omitWorkspaceFill) {
    parent.add(
      new K.Rect({
        x: ix,
        y: iy,
        width: iw,
        height: ih,
        fill: pal.workspaceFill,
        listening: false,
      }),
    );
  }
  parent.add(
    new K.Rect({
      x: ix,
      y: iy + ih - bh,
      width: iw,
      height: bh,
      fill: pal.backStrip,
      stroke: pal.outline,
      strokeWidth: 1,
      listening: false,
    }),
  );

  let ptIndex = 0;
  let ptcordx = 1;
  while (ptcordx < iw) {
    const next = pattern[ptIndex % pattern.length];
    const width = sizes[next];
    if (ptcordx + width < iw) {
      if (next === 'appliance') {
        drawV3Appliance(K, parent, ix + ptcordx, stripY, width, bh, pal);
      } else if (next === 'sink') {
        drawV3Sink(K, parent, ix + ptcordx, stripY, width, bh, pal);
      }
    }
    ptcordx += width;
    ptIndex += 1;
  }
}

/** English comment normalized. */
function drawReferenceTopWorkStrip(
  K: any,
  parent: any,
  ix: number,
  iy: number,
  iw: number,
  workH: number,
  pal: ReturnType<typeof barReferencePalette>,
): void {
  if (workH < 4 || iw < 8) {
    return;
  }
  const widths = [0.12, 0.1, 0.22, 0.18, 0.18, 0.2];
  let x = ix;
  parent.add(
    new K.Rect({
      x: ix,
      y: iy,
      width: iw,
      height: workH,
      fill: pal.workStrip,
      stroke: pal.outline,
      strokeWidth: 1,
      listening: false,
    }),
  );
  for (let s = 0; s < widths.length; s++) {
    const cw = iw * widths[s];
    parent.add(
      new K.Line({
        points: [x, iy, x, iy + workH],
        stroke: pal.segmentStroke,
        strokeWidth: 1,
        listening: false,
      }),
    );
    const shelf = s === 0 || s === 4;
    if (shelf) {
      const sh = Math.max(2, workH * 0.22);
      parent.add(
        new K.Rect({
          x: x + 1,
          y: iy - sh * 0.15,
          width: cw - 2,
          height: sh,
          fill: pal.shelfLight,
          stroke: pal.segmentStroke,
          strokeWidth: 0.5,
          listening: false,
        }),
      );
    }
    if (s === 2) {
      const pad = cw * 0.12;
      const sw = cw - pad * 2;
      const sh = workH * 0.62;
      const sx = x + pad;
      const sy = iy + workH - sh - pad * 0.4;
      const fr = sw * 0.12;
      const fx = sx + sw / 2;
      const fy = sy + sh * 0.52;
      parent.add(
        new K.Rect({
          x: sx,
          y: sy,
          width: sw,
          height: sh,
          fill: pal.sinkBasin,
          stroke: pal.sinkStroke,
          strokeWidth: 1,
          listening: false,
        }),
      );
      parent.add(
        new K.Circle({
          x: fx,
          y: fy,
          radius: fr,
          fill: pal.sinkTap,
          listening: false,
        }),
      );
    }
    x += cw;
  }
  parent.add(
    new K.Line({
      points: [ix + iw, iy, ix + iw, iy + workH],
      stroke: pal.segmentStroke,
      strokeWidth: 1,
      listening: false,
    }),
  );
}

/** English comment normalized. */
function equidistantCentersAlongSegmentLength(L: number, n: number, sd: number): number[] {
  if (n <= 0) {
    return [];
  }
  const inset = Math.max(3, sd * 0.12);
  const first = inset + sd / 2;
  const last = L - inset - sd / 2;
  if (n === 1) {
    return last >= first ? [(first + last) / 2] : [L / 2];
  }
  if (last < first) {
    return [L / 2];
  }
  const step = (last - first) / (n - 1);
  return Array.from({ length: n }, (_, i) => first + i * step);
}

function resolveBarStoolCount(explicit: number | undefined, segmentLen: number): number {
  if (explicit === undefined || explicit === null) {
    return 0;
  }
  return segmentLen > 0 ? Math.max(0, Math.trunc(explicit)) : 0;
}

function drawV3StoolAtCenter(
  K: any,
  parent: any,
  cx: number,
  cy: number,
  sd: number,
  strokeColor: string,
  stoolDomId: string,
  listening: boolean,
): void {
  parent.add(
    new K.Ellipse({
      x: cx,
      y: cy,
      radiusX: sd / 2,
      radiusY: sd / 2,
      stroke: strokeColor,
      strokeWidth: 1,
      fill: 'rgba(0,0,0,0)',
      id: stoolDomId,
      name: 'floor-plan-stool',
      listening,
    }),
  );
}

/** Mostrador cliente como una sola forma (sin costura en la esquina), alineado a la silueta L/U de v3. */
function drawCustomerCounterPolygon(
  K: any,
  parent: any,
  points: number[],
  pal: ReturnType<typeof barReferencePalette>,
  strokeW: number,
): void {
  if (points.length < 6) {
    return;
  }
  parent.add(
    new K.Line({
      points,
      closed: true,
      fill: pal.counterMain,
      stroke: pal.outline,
      strokeWidth: strokeW,
      lineJoin: 'miter',
      listening: false,
    }),
  );
}

function drawV3CustBar(
  K: any,
  parent: any,
  w: number,
  h: number,
  innerOutline: { ix: number; iy: number; iw: number; ih: number },
  dim: V3BarDimPx,
  pal: ReturnType<typeof barReferencePalette>,
  theme: UppFloorPlanTheme,
  shape: string,
): void {
  const { ix, iy, iw, ih } = innerOutline;
  const strokeW = Math.max(1.25, Math.min(2, theme.elementStrokeWidth));
  const ch = dim.customer_height;

  if (shape === 'LINEAR') {
    parent.add(
      new K.Rect({
        x: 0,
        y: h - ch,
        width: w,
        height: ch,
        fill: pal.counterMain,
        stroke: pal.outline,
        strokeWidth: 1,
        listening: false,
      }),
    );
  } else if (shape === 'TOLEFT') {
    drawCustomerCounterPolygon(
      K,
      parent,
      [0, 0, ch, 0, ch, h - ch, w, h - ch, w, h, 0, h],
      pal,
      strokeW,
    );
  } else if (shape === 'TORIGHT') {
    drawCustomerCounterPolygon(
      K,
      parent,
      [0, h - ch, 0, h, w, h, w, 0, w - ch, 0, w - ch, h - ch],
      pal,
      strokeW,
    );
  } else if (shape === 'USHAPE') {
    drawCustomerCounterPolygon(
      K,
      parent,
      [0, h, w, h, w, 0, w - ch, 0, w - ch, h - ch, ch, h - ch, ch, 0, 0, 0],
      pal,
      strokeW,
    );
  }

  if (shape === 'LINEAR') {
    const workH = Math.min(
      Math.max(dim.backend_height * 1.35, dim.section_height * 1.2),
      ih * 0.38,
    );
    parent.add(
      new K.Rect({
        x: ix,
        y: iy,
        width: iw,
        height: ih,
        fill: pal.counterMuted,
        stroke: pal.segmentStroke,
        strokeWidth: 0.5,
        listening: false,
      }),
    );
    drawReferenceTopWorkStrip(K, parent, ix, iy, iw, workH, pal);
  }

  if (shape === 'LINEAR') {
    parent.add(
      new K.Rect({
        x: 0,
        y: 0,
        width: w,
        height: h,
        fill: 'rgba(0,0,0,0)',
        stroke: pal.outline,
        strokeWidth: strokeW,
        listening: false,
      }),
    );
  }

  parent.add(
    new K.Rect({
      x: ix,
      y: iy,
      width: iw,
      height: ih,
      fill: 'rgba(0,0,0,0)',
      stroke: pal.outline,
      strokeWidth: strokeW,
      listening: false,
    }),
  );
}

/**
 * English comment normalized.
 * Each Konva ellipse uses `id` = `${barId}-stool-${n}` with n global 1…N in that order.
 */
function drawBarStoolsByShape(
  K: any,
  parent: any,
  w: number,
  h: number,
  dim: V3BarDimPx,
  pal: ReturnType<typeof barReferencePalette>,
  scalePxPerCm: number,
  shape: string,
  bar: UppFloorPlanBar,
  stoolListening: boolean,
): void {
  const sd = dim.stool_diameter;
  const clearance = t(scalePxPerCm, STOOL_CLEARANCE_BELOW_BAR_CM);
  const cyFront = h + clearance + sd / 2;
  const cxLeft = -(clearance + sd / 2);
  const cxRight = w + clearance + sd / 2;
  const barId = bar.uuid;
  let stoolIndex = 1;

  const emitFront = (n: number) => {
    for (const cx of equidistantCentersAlongSegmentLength(w, n, sd)) {
      drawV3StoolAtCenter(
        K,
        parent,
        cx,
        cyFront,
        sd,
        pal.stoolStroke,
        `${barId}-stool-${stoolIndex++}`,
        stoolListening,
      );
    }
  };
  const emitLeft = (n: number) => {
    for (const cy of equidistantCentersAlongSegmentLength(h, n, sd)) {
      drawV3StoolAtCenter(
        K,
        parent,
        cxLeft,
        cy,
        sd,
        pal.stoolStroke,
        `${barId}-stool-${stoolIndex++}`,
        stoolListening,
      );
    }
  };
  const emitRight = (n: number) => {
    const ys = equidistantCentersAlongSegmentLength(h, n, sd).reverse();
    for (const cy of ys) {
      drawV3StoolAtCenter(
        K,
        parent,
        cxRight,
        cy,
        sd,
        pal.stoolStroke,
        `${barId}-stool-${stoolIndex++}`,
        stoolListening,
      );
    }
  };

  if (shape === 'LINEAR') {
    const nF = resolveBarStoolCount(bar.stoolFront, w);
    emitFront(nF);
    return;
  }

  if (shape === 'TOLEFT') {
    const nL = resolveBarStoolCount(bar.stoolLeft, h);
    const nF = resolveBarStoolCount(bar.stoolFront, w);
    emitLeft(nL);
    emitFront(nF);
    return;
  }

  if (shape === 'TORIGHT') {
    const nF = resolveBarStoolCount(bar.stoolFront, w);
    const nR = resolveBarStoolCount(bar.stoolRight, h);
    emitFront(nF);
    emitRight(nR);
    return;
  }

  if (shape === 'USHAPE') {
    const nL = resolveBarStoolCount(bar.stoolLeft, h);
    const nF = resolveBarStoolCount(bar.stoolFront, w);
    const nR = resolveBarStoolCount(bar.stoolRight, h);
    emitLeft(nL);
    emitFront(nF);
    emitRight(nR);
  }
}

/**
 * Stools above the hit-rect of the bbox so they receive the pointer when {@link FloorPlanShapePaintContext.elementPointerListening} is true.
 */
export function paintBarStoolsOnGroup(ctx: FloorPlanShapePaintContext): void {
  const bar = ctx.element;
  if (!(bar instanceof UppFloorPlanBar)) {
    return;
  }
  const { K, group, widthPx: w, heightPx: h, scalePxPerCm, theme } = ctx;
  const dim = v3BarDimensionsPx(scalePxPerCm);
  const pal = barReferencePalette(theme);
  const listen = ctx.elementPointerListening === true;
  drawBarStoolsByShape(K, group, w, h, dim, pal, scalePxPerCm, bar.barShape, bar, listen);
}

/**
 * v3-style bar rendering: customer counter + back strip with work area (appliances, sink, shelves).
 */
export function paintBarV3Like(ctx: FloorPlanShapePaintContext): void {
  const { K, group, widthPx: w, heightPx: h, scalePxPerCm, theme, element } = ctx;
  const bar = element as UppFloorPlanBar;
  const shape = bar.barShape;

  const dim = v3BarDimensionsPx(scalePxPerCm);
  const ch = dim.customer_height;
  const pal = barReferencePalette(theme);

  const innerOutline = v3InnerAfterShape(w, h, ch, shape);
  const innerBack = v3InnerShrunkForBack(innerOutline, shape);

  drawV3CustBar(K, group, w, h, innerOutline, dim, pal, theme, shape);

  if (shape === 'LINEAR') {
    drawV3BackEnd(K, group, innerBack.ix, innerBack.iy, innerBack.iw, innerBack.ih, dim, pal, {
      omitWorkspaceFill: true,
    });
  } else {
    drawV3BackEnd(K, group, innerBack.ix, innerBack.iy, innerBack.iw, innerBack.ih, dim, pal);
    drawV3BackBar(K, group, innerBack.ix, innerBack.iy, innerBack.iw, innerBack.ih, dim, pal);
  }
}

for (const key of ['bar:LINEAR', 'bar:TOLEFT', 'bar:TORIGHT', 'bar:USHAPE'] as const) {
  registerFloorPlanShapePainter(key, paintBarV3Like);
}

/**
 * Resolves the stool index from the Konva node target (used for bar stool pointer events).
 */
export function floorPlanBarStoolIndexFromPointerTarget(bar: UppFloorPlanBar, target: unknown): number | null {
  const id = typeof (target as { id?: () => string })?.id === 'function' ? (target as { id: () => string }).id() : '';
  const prefix = `${bar.uuid}-stool-`;
  if (typeof id === 'string' && id.startsWith(prefix)) {
    const n = parseInt(id.slice(prefix.length), 10);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}
